import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Calendar, Clock, User, X, Edit2, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { api } from '../lib/api';

const OfflineClasses = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  
  // Filters
  const [batchFilter, setBatchFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    topic: '',
    batch_id: '',
    teacher_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60
  });
  
  const [statusData, setStatusData] = useState({
    status: '',
    actual_duration_minutes: '',
    cancellation_reason: ''
  });

  const statusOptions = ['Scheduled', 'Run as per Plan', 'Cancelled', 'Proxy Teacher Used'];

  useEffect(() => {
    fetchClasses();
    fetchBatches();
    fetchTeachers();
  }, [batchFilter, teacherFilter, statusFilter, dateFilter]);

  const fetchClasses = async () => {
    try {
      const params = {};
      if (batchFilter) params.batch_id = batchFilter;
      if (teacherFilter) params.teacher_id = teacherFilter;
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) params.date = dateFilter;
      
      const response = await api.getOfflineClasses(params);
      setClasses(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch classes');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.getOfflineBatches({ active_only: true });
      setBatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await api.getAllTeachers();
      setTeachers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch teachers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Combine date and time
      const scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);
      
      const payload = {
        title: formData.title,
        subject: formData.subject,
        topic: formData.topic,
        batch_id: formData.batch_id,
        teacher_id: formData.teacher_id,
        scheduled_date: scheduledDateTime.toISOString(),
        duration_minutes: parseInt(formData.duration_minutes)
      };
      
      if (selectedClass) {
        await api.updateOfflineClass(selectedClass.id, payload);
        toast.success('Class updated successfully');
      } else {
        await api.createOfflineClass(payload);
        toast.success('Class created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateOfflineClassStatus(
        selectedClass.id,
        statusData.status,
        statusData.actual_duration_minutes || null,
        statusData.cancellation_reason || null
      );
      toast.success('Class status updated');
      setShowStatusModal(false);
      setStatusData({ status: '', actual_duration_minutes: '', cancellation_reason: '' });
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.deleteOfflineClass(id);
      toast.success('Class deleted');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    }
  };

  const openEditModal = (cls) => {
    const date = new Date(cls.scheduled_date);
    setSelectedClass(cls);
    setFormData({
      title: cls.title,
      subject: cls.subject,
      topic: cls.topic || '',
      batch_id: cls.batch_id,
      teacher_id: cls.teacher_id,
      scheduled_date: date.toISOString().split('T')[0],
      scheduled_time: date.toTimeString().slice(0, 5),
      duration_minutes: cls.duration_minutes
    });
    setShowModal(true);
  };

  const openStatusModal = (cls) => {
    setSelectedClass(cls);
    setStatusData({
      status: cls.status,
      actual_duration_minutes: cls.actual_duration_minutes || '',
      cancellation_reason: cls.cancellation_reason || ''
    });
    setShowStatusModal(true);
  };

  const resetForm = () => {
    setSelectedClass(null);
    setFormData({
      title: '',
      subject: '',
      topic: '',
      batch_id: '',
      teacher_id: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'bg-blue-100 text-blue-700';
      case 'Run as per Plan': return 'bg-green-100 text-green-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      case 'Proxy Teacher Used': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="offline-classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Offline Classes</h1>
          <p className="text-slate-600">Schedule and manage offline centre classes</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
          data-testid="add-class-btn"
        >
          <Plus size={20} />
          Schedule Class
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="">All Batches</option>
            {batches.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="">All Teachers</option>
            {teachers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          >
            <option value="">All Status</option>
            {statusOptions.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg"
          />
          {(batchFilter || teacherFilter || statusFilter || dateFilter) && (
            <button
              onClick={() => { setBatchFilter(''); setTeacherFilter(''); setStatusFilter(''); setDateFilter(''); }}
              className="px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Classes List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading...</div>
        ) : classes.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No classes found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Class</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Batch</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Teacher</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date & Time</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Duration</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {classes.map((cls) => (
                  <tr key={cls.id} className="hover:bg-slate-50" data-testid={`class-row-${cls.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{cls.title}</div>
                      <div className="text-sm text-slate-500">{cls.subject} {cls.topic && `- ${cls.topic}`}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                        {cls.batch_name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{cls.teacher_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(cls.scheduled_date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock size={14} />
                        {new Date(cls.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {cls.actual_duration_minutes || cls.duration_minutes} min
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openStatusModal(cls)}
                        className={`px-2 py-1 text-xs rounded-full ${getStatusColor(cls.status)}`}
                      >
                        {cls.status}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(cls)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(cls.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Class Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">{selectedClass ? 'Edit Class' : 'Schedule New Class'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Class Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Topic</label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData({...formData, topic: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Batch *</label>
                <select
                  value={formData.batch_id}
                  onChange={(e) => setFormData({...formData, batch_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                >
                  <option value="">Select Batch</option>
                  {batches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teacher *</label>
                <select
                  value={formData.teacher_id}
                  onChange={(e) => setFormData({...formData, teacher_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                >
                  <option value="">Select Teacher</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time *</label>
                  <input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  min="15"
                  max="480"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  {selectedClass ? 'Update' : 'Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && selectedClass && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Update Class Status</h2>
              <button onClick={() => setShowStatusModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 bg-slate-50 border-b">
              <p className="font-medium text-slate-800">{selectedClass.title}</p>
              <p className="text-sm text-slate-500">{selectedClass.teacher_name} - {selectedClass.batch_name}</p>
            </div>
            <form onSubmit={handleStatusUpdate} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={statusData.status}
                  onChange={(e) => setStatusData({...statusData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  required
                >
                  {statusOptions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {statusData.status === 'Run as per Plan' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Actual Duration (minutes)</label>
                  <input
                    type="number"
                    value={statusData.actual_duration_minutes}
                    onChange={(e) => setStatusData({...statusData, actual_duration_minutes: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    placeholder={`Planned: ${selectedClass.duration_minutes} min`}
                  />
                </div>
              )}
              {statusData.status === 'Cancelled' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cancellation Reason</label>
                  <textarea
                    value={statusData.cancellation_reason}
                    onChange={(e) => setStatusData({...statusData, cancellation_reason: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    rows={3}
                  />
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowStatusModal(false)} className="flex-1 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  Update Status
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineClasses;
